import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import { trackStructuredEvent } from 'src/analytics';
import useCompletion from './hooks/useCompletion';
import useInterview from '../../hooks/useInterview';
import { HelpfulQuestion } from '@buoy-components/user-feedback';
import { getCategory } from '../../analytics';
import CollapsibleResults from '../CollapsibleResults';
import ErrorContainer from '../ErrorContainer';
import { TreatmentCard } from '../Completion/components/TreatmentsForYou/TreatmentsForYou';
import Pill from '../Completion/DiagnosisOfConcern/components/Pill';
import { generateConclusionReasons } from './utils';
import useChatList from './hooks/useChatList';
import SubmitButton from '../SubmitButton';
import { useCallback } from 'react';
import { trackPartnerCardClicked, trackPartnerCardViewed } from './analytics';

import type { MiniInterviewType } from 'src/packages/polaris/types';

const Completion = ({
  triggerRestart,
  conditionName,
}: {
  triggerRestart: () => void;
  conditionName?: string;
}) => {
  const completion = useCompletion();
  const interview = useInterview();
  const { results } = completion;
  const chatList = useChatList();

  const reasons = generateConclusionReasons(chatList.items, conditionName);

  const onTreatmentCardClick = useCallback(
    (args: {
      impressionId: string;
      buttonLabel: string;
      url?: string | undefined;
      interviewType: MiniInterviewType;
    }) => {
      trackPartnerCardClicked({ interviewToken: interview.token || '', url: args.url });
    },
    [interview.token],
  );

  const onTreatmentCardViewed = useCallback(
    (args: {
      impressionId: string;
      order: number;
      url?: string | undefined;
      interviewType: MiniInterviewType;
    }) => {
      trackPartnerCardViewed({ interviewToken: interview.token || '', url: args.url });
    },
    [interview.token],
  );

  return (
    <Grid item className="bp-flex bp-h-full bp-grow bp-flex-col bp-pr-2 bp-pt-0 bp-font-primary">
      <CollapsibleResults isShowingResults>
        <Grid container direction="column" spacing={2}>
          {results?.length ? (
            <>
              <Grid item className="bp-text-lg">
                Based on your answers, the below treatment option might be appropriate for your
                situation.
              </Grid>
              {!!reasons.length && (
                <Grid item className="bp-text-base">
                  Chosen for you because:
                  <div className="bp-my-2 bp-flex bp-flex-wrap bp-gap-2">
                    {reasons.map((reason) => (
                      <Pill key={reason} label={reason} variant="check" />
                    ))}
                  </div>
                </Grid>
              )}
              {results?.map((result, i) => (
                <Grid item key={i}>
                  <TreatmentCard
                    index={i}
                    treatment={result}
                    containerClassName="bp-max-w-[363px]"
                    onClick={onTreatmentCardClick}
                    onViewed={onTreatmentCardViewed}
                    interviewType="pxoc"
                  />
                </Grid>
              ))}
            </>
          ) : (
            <Grid item>
              <ErrorContainer
                headline="Hmm, we can't seem to find a good match for you."
                triggerRestart={triggerRestart}
              />
            </Grid>
          )}
          <Grid item>
            <Divider className="bp-mb-4" />
            <HelpfulQuestion
              snowplowProps={{
                action: getCategory('pxoc'),
                label: interview.token || '',
              }}
              trackingCallback={trackStructuredEvent}
              withBuoyTheme
            />
          </Grid>
        </Grid>
        <SubmitButton isRestart onClick={triggerRestart} text="Restart" />
      </CollapsibleResults>
    </Grid>
  );
};

export default Completion;
